import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { trackEvent } from '../../services/trackEvent';
import CheckIcon from '@mui/icons-material/Check';
import { Menu as DensityIcon} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddUser from '../../components/AddUser';
import InviteUsers from '../../components/InviteUsers';
import {
  userListService,
  jobFunctionService,
  User,
  JobFunction, 
  UserRole,
  AUTH_ERRORS
} from '../../services/axiosService';
import { Application } from '../../constants/Application';
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridFilterModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridRenderCellParams,
  useGridApiContext,
  GridToolbarQuickFilter,
  ToolbarButton,
  Toolbar,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  QuickFilterTrigger,
  FilterPanelTrigger,
  ColumnsPanelTrigger,
  ExportCsv,
  ExportPrint,
  GridDensity,
  useGridSelector,
  gridDensitySelector,
  GridExpandMoreIcon,
  GridSlotsComponentsProps
} from '@mui/x-data-grid';
import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Import additional icons
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

type OwnerState = {
  expanded: boolean;
};

const StyledQuickFilter = styled(QuickFilter)(({ theme }) => ({
  display: 'grid',
  alignItems: 'center',
  marginLeft: 'auto',
  width: '100%',
}));

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
  }),
);

const StyledTextField = styled(TextField)<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

// augment the props for the toolbar slot
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onOpenAddUser: () => void;
    onInviteUsers: () => void;
  }
}

function ExportMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const theme = useTheme();

  const handleExportClick = (exportType: string) => {
    trackEvent.exportClicked(exportType, 0, {
      filterActive: false,
      sortActive: false
    });
  };

  return (
    <React.Fragment>
      <Tooltip title={t('usersPage.toolbar.export')}>
        <ToolbarButton
          ref={triggerRef}
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={() => setOpen(true)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FileDownloadIcon fontSize="small" sx={{ color: 'e3.blueDark' }} />
              <Typography variant="button" sx={{ fontWeight: 'medium', color: 'e3.blueDark' }}>
                {t('usersPage.toolbar.export')}
              </Typography>
          </Box>
        </ToolbarButton>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': 'export-menu-trigger',
          },
        }}
      >
        <ExportCsv
          render={<MenuItem />}
          onClick={() => handleExportClick('CSV')}
        >
          {t('usersPage.export.downloadCSV')}
        </ExportCsv>
        {/* <ExportExcel render={<MenuItem />}>Download as Excel</ExportExcel> */}
        {/* <ExportPrint
          render={<MenuItem />}
          onClick={() => handleExportClick('Print')}
        >
          {t('usersPage.export.print')}
        </ExportPrint> */}
      </Menu>
    </React.Fragment>
  );
}

const DENISTY_OPTIONS: { labelKey: string; value: 'compact' | 'standard' | 'comfortable'; padding: number }[] = [
  { labelKey: 'usersPage.density.compact', value: 'compact', padding: 0.5 },
  { labelKey: 'usersPage.density.standard', value: 'standard', padding: 1 },
  { labelKey: 'usersPage.density.comfortable', value: 'comfortable', padding: 2 },
];

// Helper function to get padding value for a density
const getDensityPadding = (density: 'compact' | 'standard' | 'comfortable'): number => {
  const option = DENISTY_OPTIONS.find(opt => opt.value === density);
  return option ? option.padding : 1; // Default to standard padding if not found
};

// Custom toolbar component with density selector
const CustomToolbar = (
  props: NonNullable<GridSlotsComponentsProps['toolbar']>
) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const density = useGridSelector(apiRef, gridDensitySelector);
  const [densityMenuOpen, setDensityMenuOpen] = React.useState(false);
  const densityMenuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const { userRole } = useUser();

  return (
    <Toolbar>
      <Box sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        p: 1,
        flexDirection: isSmallScreen ? 'column' : 'row',
        gap: 2
      }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'nowrap',
          justifyContent: isSmallScreen ? 'space-between' : 'flex-start',
          width: isSmallScreen ? '100%' : 'auto',
          flex: isSmallScreen ? 'none' : '1 1 auto'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: isSmallScreen ? '1 1 0' : '0 0 auto' }}>
            <Tooltip title={t('usersPage.toolbar.columns')}>
              <Box sx={{ color: 'e3.blueDark', display: 'flex', alignItems: 'center' }}>
                <ColumnsPanelTrigger render={<ToolbarButton />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ViewColumnIcon fontSize="small" sx={{ color: 'e3.blueDark' }} />
                      <Typography variant="button" sx={{ fontWeight: 'medium', color: 'e3.blueDark' }}>
                        {t('usersPage.toolbar.columns')}
                      </Typography>
                  </Box>
                </ColumnsPanelTrigger>
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: isSmallScreen ? '1 1 0' : '0 0 auto' }}>
            <Tooltip title={t('usersPage.toolbar.filters')}>
              <Box sx={{ color: 'e3.blueDark', display: 'flex', alignItems: 'center' }}>
                <FilterPanelTrigger render={<ToolbarButton />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FilterListIcon fontSize="small" sx={{ color: 'e3.blueDark' }} />
                      <Typography variant="button" sx={{ fontWeight: 'medium', color: 'e3.blueDark' }}>
                        {t('usersPage.toolbar.filters')}
                      </Typography>
                  </Box>
                </FilterPanelTrigger>
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: isSmallScreen ? '1 1 0' : '0 0 auto' }}>
            <Tooltip title={t('usersPage.toolbar.density')}>
              <Box sx={{ color: 'e3.blueDark', display: 'flex', alignItems: 'center' }}>
                <ToolbarButton
                  ref={densityMenuTriggerRef}
                  id="density-menu-trigger"
                  aria-controls="density-menu"
                  aria-haspopup="true"
                  aria-expanded={densityMenuOpen ? 'true' : undefined}
                  onClick={() => setDensityMenuOpen(true)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DensityIcon fontSize="small" sx={{ color: 'e3.blueDark' }} />
                      <Typography variant="button" sx={{ fontWeight: 'medium', color: 'e3.blueDark' }}>
                        {t('usersPage.toolbar.density')}
                      </Typography>
                  </Box>
                </ToolbarButton>
              </Box>
            </Tooltip>

            <Menu
              id="density-menu"
              anchorEl={densityMenuTriggerRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={densityMenuOpen}
              onClose={() => setDensityMenuOpen(false)}
              slotProps={{
                list: {
                  'aria-labelledby': 'density-menu-trigger',
                },
              }}
            >
              {DENISTY_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    apiRef.current.setDensity(option.value);
                    setDensityMenuOpen(false);
                  }}
                >
                  <ListItemIcon>
                    {density === option.value && <CheckIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{t(option.labelKey)}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: isSmallScreen ? '1 1 0' : '0 0 auto' }}>
            <ExportMenu />
          </Box>
          
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          width: isSmallScreen ? '100%' : 'auto',
          marginLeft: isSmallScreen ? '0' : 'auto',
          flex: isSmallScreen ? 'none' : '0 1 auto'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: isSmallScreen ? '1 1 auto' : 'none',
            width: isSmallScreen ? '100%' : 'auto',
            maxWidth: isSmallScreen ? 'calc(100% - 150px)' : 'auto'
          }}>
            <StyledQuickFilter>
              <QuickFilterControl
                render={({ ref, ...controlProps }, state) => (
                  <StyledTextField
                    {...controlProps}
                    ownerState={{ expanded: true }}
                    variant='standard'
                    inputRef={ref}
                    aria-label="Search"
                    placeholder={t('usersPage.toolbar.search')}
                    size="medium"
                    fullWidth
                    sx={{ width: '100%' }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: state.value ? (
                          <InputAdornment position="end">
                            <QuickFilterClear
                              edge="end"
                              size="small"
                              aria-label="Clear search"
                              material={{ sx: { marginRight: -0.75 } }}
                            >
                              <CancelIcon fontSize="small" />
                            </QuickFilterClear>
                          </InputAdornment>
                        ) : null,
                        ...controlProps.slotProps?.input,
                      },
                      ...controlProps.slotProps,
                    }}
                  />
                )}
              />
            </StyledQuickFilter>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '0 0 auto',
            gap: 1
          }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={props.onInviteUsers}
              sx={{
                width: 'auto'
              }}
            >
              {t('usersPage.toolbar.inviteUsers')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={props.onOpenAddUser}
              sx={{
                width: 'auto'
              }}
            >
              {t('usersPage.toolbar.createNewUser')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Toolbar>
  );
};

const Users: React.FC = () => {
  const { t } = useTranslation();
  
  // DataGrid localization
  const dataGridLocaleText = useMemo(() => ({
    // Pagination 
    MuiTablePagination: {
      labelRowsPerPage: t('common.rowsPerPage'),
    },
    // Also add the standard MUI property
    labelRowsPerPage: t('common.rowsPerPage'),
    // Toolbar
    toolbarColumns: t('usersPage.toolbar.columns'),
    toolbarFilters: t('usersPage.toolbar.filters'),
    toolbarDensity: t('usersPage.toolbar.density'),
    toolbarExport: t('usersPage.toolbar.export'),
    // Column menu
    olumnMenuShowColumns: t('common.showColumns'),
    columnMenuFilter: t('common.filter'),
    columnMenuHideColumn: t('common.hideColumn'),
    columnMenuUnsort: t('common.unsort'),
    columnMenuSortAsc: t('common.sortAsc'),
    columnMenuSortDesc: t('common.sortDesc'),
    // Filter
    filterPanelColumns: t('common.columns'),
    filterPanelOperator: t('common.operator'),
    filterPanelInputLabel: t('common.value'),
    filterPanelInputPlaceholder: t('common.filterValue'),
    filterOperatorContains: t('common.contains'),
    filterOperatorEquals: t('common.equals'),
    filterOperatorStartsWith: t('common.startsWith'),
    filterOperatorEndsWith: t('common.endsWith'),
    filterOperatorIsEmpty: t('common.isEmpty'),
    filterOperatorIsNotEmpty: t('common.isNotEmpty'),
       // Pagination
    footerRowsPerPage: t('common.rowsPerPage'),
    footerPaginationRowsPerPage: t('common.rowsPerPage'),
    columnsPanel: {
    showAllButton: 'Show/Hide All (i18n)',
    hideAllButton: 'Reset (i18n)',
    search: 'Search… (i18n)',
  },
  }), [t]);
  const { getAccessToken } = useAuth();
  const { userRole } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [jobFunctions, setJobFunctions] = useState<JobFunction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccount } = useUser();
  const [density, setDensity] = useState<'compact' | 'standard' | 'comfortable'>('standard');
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editUserId, setEditUserId] = useState<string | undefined>(undefined);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  
  // No separate loading state needed as we'll add it to the user objects
  
  // Default application is set to Protect as per requirements
  const [currentApplication, setCurrentApplication] = useState<string>(Application.Protect);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'userName',
      sort: 'asc',
    },
  ]);

  // Track page view on component mount
  useEffect(() => {
    trackEvent.pageViewed('Users Page', {
      application: currentApplication,
      userRole: userRole?.isAdmin ? 'Admin' : 'Basic User'
    });
  }, [currentApplication, userRole]);

  // Track filter changes
  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length > 0) {
      newFilterModel.items.forEach(filter => {
        trackEvent.filterUsed(filter.field || 'unknown', filter.value || '', users.length, {
          operator: filter.operator,
          application: currentApplication
        });
      });
    }
    setFilterModel(newFilterModel);
  };

  // Track sort changes
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (newSortModel.length > 0) {
      newSortModel.forEach(sort => {
        trackEvent.sortApplied(sort.field, sort.sort, {
          application: currentApplication,
          userRole: userRole?.isAdmin ? 'Admin' : 'Basic User',
          totalUsers: users.length,
          hasActiveFilters: filterModel.items.length > 0,
          sortCount: newSortModel.length,
          page: 'Users'
        });
      });
    } else {
      // Track when sorting is cleared
      trackEvent.sortCleared({
        application: currentApplication,
        userRole: userRole?.isAdmin ? 'Admin' : 'Basic User',
        totalUsers: users.length,
        page: 'Users'
      });
    }
    setSortModel(newSortModel);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    //setEditUserId(undefined); // Clear edit mode
  };

  const handleOpenAddUser = () => {
    setEditUserId(undefined); // Clear edit mode for add
    setOpenUserDialog(true);
  };

  const handleInviteUsers = () => {
    setOpenInviteDialog(true);
    trackEvent.custom('CUM Invite Users Clicked', {
      application: currentApplication,
      userRole: userRole?.isAdmin ? 'Admin' : 'Basic User'
    });
  };

  const handleCloseInviteDialog = () => {
    setOpenInviteDialog(false);
  };

  const handleInvitationSent = () => {
    // Refresh users data after invitations are sent
    fetchData();
  };

  const handleRowClick = (params: any) => {
    setEditUserId(params.row.id); // Set the user ID for editing
    setOpenUserDialog(true);
  };

  // Fetch users and job functions data
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      
      if (!token) {
        throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      }
      const account = getAccount(currentApplication);
      // Fetch users and job functions in parallel
      // Pass the currentApplication parameter to getAllUsers
      const [usersData, jobFunctionsData] = await Promise.all([
        userListService.getAllUsers(token, account),
        jobFunctionService.getAllJobFunctions(token)
      ]);
      
      setUsers(usersData);
      setJobFunctions(jobFunctionsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError("apiResponse.UserListFetchError");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAccessToken, currentApplication]); // Add currentApplication as a dependency

  // Callback function to refresh users data after Add/Edit operations
  const handleUserSaved = () => {
    fetchData();
  };

  // Status cell renderer
  const renderStatusCell = (params: GridRenderCellParams) => {
    const status = params.value as string;
    
    // Only display content if status is not 'Active'
    if (status === 'Active') {
      return null;
    }
    
    return (
      <Typography
        variant="body2"
        fontStyle={status === 'Deactivated' ? 'italic' : undefined}
        color={status === 'Pending' ? 'warning.main' : undefined}
        fontWeight={status === 'Pending' ? 'bold' : undefined}
      >
        {status}
      </Typography>
    );
  };

  // Define columns
  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'userName',
        headerName: t('usersPage.columns.userName'),
        flex: 1,
        minWidth: 150,
        filterable: true,
        sortable: true,
      },
      {
        field: 'email',
        headerName: t('usersPage.columns.email'),
        flex: 1.5,
        minWidth: 200,
        filterable: true,
        sortable: true,
      },
      {
        field: 'firstName',
        headerName: t('usersPage.columns.firstName'),
        flex: 1,
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        field: 'lastName',
        headerName: t('usersPage.columns.lastName'),
        flex: 1,
        minWidth: 120,
        filterable: true,
        sortable: true,
      },
      {
        field: 'jobFunction',
        headerName: t('usersPage.columns.jobFunction'),
        flex: 1,
        minWidth: 180,
        filterable: true,
        sortable: true,
        type: 'singleSelect',
        valueOptions: jobFunctions.map((jf) => jf.jobFunction),
      },
    ];

    const productAccessColumn: GridColDef = {
      field: 'productAccess',
      headerName: t('usersPage.columns.productAccess'),
      flex: 1.5,
      minWidth: 200,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const userId = params.row.id as string;
        const oktaUserId = params.row.oktaUserId as string;
        const currentValue = params.value as string;
        
        // Function to load product access data
        const handleLoadProductAccess = () => {
          // Set loading state directly on the user object
          setUsers(prevUsers =>
            prevUsers.map(user =>
              user.id === userId
                ? { ...user, isLoadingProductAccess: true }
                : user
            )
          );
          
          // Use setTimeout to allow the loading state to be rendered
          setTimeout(async () => {
            try {
              // Get access token
              const token = await getAccessToken();
              if (!token) {
                throw new Error('Session expired. Please log in again.');
              }

              // Fetch product access data using the oktaUserId
              const productAccessData = await userListService.getUserProductAccess(token, oktaUserId);

            // Update the users array to reflect the change
            setUsers(prevUsers =>
              prevUsers.map(user =>
                user.id === userId
                  ? { ...user, productAccess: productAccessData }
                  : user
              )
            );
            
            } catch (error) {
              console.error('Error fetching product access:', error);
              // Set error state or show notification if needed
            } finally {
              // Clear loading state
              setUsers(prevUsers =>
                prevUsers.map(user =>
                  user.id === userId
                    ? { ...user, isLoadingProductAccess: false }
                    : user
                )
              );
            }
          }, 0);
        };
        
        // If currently loading, show loading indicator
        const isLoading = params.row.isLoadingProductAccess === true;
        
        if (isLoading) {
          return (
            <Box sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'left'
            }}>
              <CircularProgress size={20} />
            </Box>
          );
        }
        
        // If the current value is not empty, show it as colored chips
        if (currentValue) {
          // Handle both string and array types for backward compatibility
          const apps = Array.isArray(currentValue) ? currentValue :
                       (typeof currentValue === 'string' && currentValue !== '') ? [currentValue] : [];
          
          if (apps.length > 0) {
            // Color mapping for different applications
            const chipColor = 'default';
            
            return (
              <Box sx={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5
              }}>
                {apps.map((app, index) => (
                  <Chip
                    key={`${userId}-${app}-${index}`}
                    label={app}
                    color={chipColor}
                    size="small"
                    variant="outlined"
                    clickable={false}
                    onClick={(e) => {
                    }}
                  />
                ))}
              </Box>
            );
          }
        }
        
        
        // If oktaUserId is null, undefined, or empty, show nothing
        if (!oktaUserId) {
          return null;
        }
        
        // Show the button to load data
        return (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'left', justifyContent: 'left' }}>
            <Tooltip title={t('usersPage.actions.viewProductAccess')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row selection
                  handleLoadProductAccess();
                }}
                data-testid={`view-product-access-${userId}`}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    };

    const statusColumn: GridColDef = {
      field: 'status',
      headerName: t('usersPage.columns.status'),
      flex: 0.8,
      minWidth: 120,
      filterable: true,
      sortable: true,
      renderCell: renderStatusCell,
      type: 'singleSelect',
      valueOptions: ['Deactivated', 'Pending'],
    };

    
    if (currentApplication === Application.Protect) {
      return [
        ...baseColumns,
        {
          field: 'role',
          headerName: t('usersPage.columns.userGroup'),
          flex: 1,
          minWidth: 120,
          filterable: true,
          sortable: true,
        },
        {
          field: 'location',
          headerName: t('usersPage.columns.location'),
          flex: 1,
          minWidth: 150,
          filterable: true,
          sortable: true,
        },
        statusColumn
      ];
    }

    return [...baseColumns, statusColumn]; 
  }, [jobFunctions, currentApplication, t]); // Add 't' dependency for translations

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {t(error, error)}
        </Typography>
      </Box>
    );
  }


return (
    <>
      <DataGrid
        rows={users}
        columns={columns}
        localeText={dataGridLocaleText}
        
        showToolbar
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{ toolbar: { onOpenAddUser: handleOpenAddUser, onInviteUsers: handleInviteUsers } }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
          density: 'standard', // Set default density (options: 'compact', 'standard', 'comfortable')
        }}
        
        density={density}
        onDensityChange={(newDensity) => setDensity(newDensity)}
        getRowHeight={() => 'auto'}
        virtualizeColumnsWithAutoRowHeight
        pageSizeOptions={[25, 50, 100]}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick={true} // Disable row selection
        disableColumnMenu={false} // Enable column menu for column management
        checkboxSelection={false}
        sx={{
          minWidth: '460px',
          '& .MuiDataGrid-cell': {
            py: (theme) => theme.spacing(getDensityPadding(density)),
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'visible',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-cell--withRenderer': {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-cellContent': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          // Make headers stand out and add top border
          '& .MuiDataGrid-columnHeaders': {
            fontWeight: '600',
            fontSize: '1rem',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600',
          },
          // Make rows look clickable with hover effects
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover'
            },
          },
        }}
      />
      <AddUser
        open={openUserDialog}
        onClose={handleCloseUserDialog}
        userId={editUserId}
        onUserSaved={handleUserSaved}
      />
      <InviteUsers
        open={openInviteDialog}
        onClose={handleCloseInviteDialog}
        onInvitationSent={handleInvitationSent}
      />
    </>
  );
};

export default Users;